import { useRestAPI } from "@src/frontend/hooks/useRestAPI";
import { REQUIRED_RULES } from "@src/frontend/utils";
import type { ScriptResourceModel } from "@src/generated/prisma/models";
import { App, Flex, Form, Input, Modal, Radio, Select } from "antd";
import React, { useCallback, useImperativeHandle, useMemo, useState } from "react";

interface Props {}

export interface QuickAddRef {
  handleOpenModal: (title: string, keys: string[]) => void;
}

export const ResourceQuickAdd = React.forwardRef((props: Props, ref: React.Ref<QuickAddRef>) => {
  const { listReq, updateReq, createReq } = useRestAPI<ScriptResourceModel & { data: { key: string; value: string }[] }>('/api/resources');
  const [form] = Form.useForm<ScriptResourceModel & { data: { key: string; value: string }[] }>();
  const [open, setOpen] = useState(false);
  const [appendType, setAppendType] = useState<AppendType>('create');
  const appendTypeOptions = useMemo(() => [
    { label: '创建新资源', value: 'create' },
    { label: '追加到已有资源', value: 'append', disabled: !listReq.data?.data?.length },
  ], [listReq]);
  const selectOptions = useMemo(() => {
    return listReq.data?.data?.map(e => ({ label: e.name, value: e.id }));
  }, [listReq]);
  const handleOpenModal = useCallback(async (title: string, keys: string[]) => {
    console.debug('openModal', title, keys);
    form.resetFields();
    form.setFieldsValue({
      name: title,
      data: keys.map(key => ({ key, value: '' })),
    });
    setOpen(true);
  }, [form]);
  useImperativeHandle(ref, () => ({ handleOpenModal }))
  const { message } = App.useApp();
  const handleOk = useCallback(async () => {
    const { id, name, data = [] } = await form.validateFields();
    console.debug('handleOk', { id, name, data, appendType });
    if (appendType === 'create') {
      await createReq.runAsync({ name, data });
      message.success('创建成功！');
    } else if (appendType === 'append' && id) {
      const oldData = listReq.data?.data?.find(e => e.id === id);
      await updateReq.runAsync({ name: oldData?.name || name, data: [...oldData?.data || [], ...data ] }, id);
      message.success('追加成功！');
    }
    await listReq.runAsync();
  }, [appendType, form, listReq.data?.data, createReq, updateReq]);
  return (
    <>
      <Modal
        forceRender
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleOk}
      >
        <Flex vertical gap={12}>
          <Flex justify="center">
            <Radio.Group
              optionType="button"
              value={appendType}
              options={appendTypeOptions}
              onChange={v => setAppendType(v.target.value)}
            />
          </Flex>
          <Form layout="vertical" form={form}>
            { appendType === 'create' && (
              <Form.Item rules={[REQUIRED_RULES]} label='名称' name={'name'}>
                <Input />
              </Form.Item>
            )}
            {appendType === 'append' && (
              <Form.Item label='添加至已有资源' name={'id'}>
                <Select
                  options={selectOptions}
                  showSearch={{
                    filterOption: (input, option) => Boolean(option?.label?.includes(input)),
                  }}
                />
              </Form.Item>
            )}
            <Form.List name={'data'}>
             {(fields) => (
                <Flex vertical align="start" justify="start">
                  {fields.map(({ name, key }, i) => (
                    <Flex key={key} flex={1} gap={8} style={{ width: '100%' }}>
                      <Form.Item style={{ width: '100%' }} rules={[REQUIRED_RULES]} label='键' name={[name, 'key']}>
                        <Input disabled />
                      </Form.Item>
                      <Form.Item style={{ width: '100%' }} rules={[REQUIRED_RULES]} label='值' name={[name, 'value']}>
                        <Input />
                      </Form.Item>
                    </Flex>
                  ))}
                </Flex>
             )}
            </Form.List>
          </Form>
        </Flex>
      </Modal>
    </>
  );
});

type AppendType = 'create' | 'append';